import numeral from 'numeral';
import { join, slice, startsWith } from 'lodash';
import { adminApi } from './axios';

export const textCapitalize = (text) => text.toLowerCase().replace(/\b./g, (a) => a.toUpperCase());

export const getFormData = (object) => {
  const formData = new FormData();
  Object.keys(object).forEach((key) => formData.append(key, object[key]));
  return formData;
};

export const isWebsiteUrl = (url) => url.includes('http');

export const getFileNameFromUrl = (url) => url.split('/').pop();

export const symbolFormat = (value, currency) => {
  const formattedValue = numeral(value).format(
    Number.isInteger(value) ? '0,0' : `0,0.${'0'.repeat(currency?.decimal_place || 2)}`
  );
  if (currency?.symbol_prefix) {
    return `${currency?.symbol} ${formattedValue}`;
  }
  return `${formattedValue} ${currency?.symbol}`;
};

export const numricRegex = /(?=.*[0-9])/;

export const removeCodeFromPhone = (phone, code) => {
  if (startsWith(phone, code)) {
    return join(slice(phone, code.length), '');
  }
  return phone;
};

export const truncateMessage = (message, maxLength = 50) => {
  if (!message) return '';
  // Remove line breaks for truncated view
  const cleanMessage = message.replace(/\\r\\n|\\n/g, ' ');
  if (cleanMessage.length > maxLength) {
    return `${cleanMessage.substring(0, maxLength)}...`;
  }
  return cleanMessage;
};

// Helper function to parse errors
export const parseErrorsWithFieldNames = (errors) => {
  const messages = [];

  Object.entries(errors).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Field-specific errors (e.g., { username: ["This field is required."] })
      value.forEach((message) => {
        messages.push({ field: key, message });
      });
    } else if (typeof value === 'object' && value !== null) {
      // Nested errors (e.g., { user: { email: ["Invalid email"] } })
      const nestedMessages = parseErrorsWithFieldNames(value);
      nestedMessages.forEach((nestedError) => {
        messages.push({
          field: `${key}.${nestedError?.field}`,
          message: nestedError?.message,
        });
      });
    } else if (typeof value === 'string') {
      // String errors (less common)
      messages.push({ field: key, message: value });
    }
  });

  return messages;
};

// Common error handler function
// Handles various error formats and displays backend messages as-is:
// 1. { detail: "message", code: "error_code" } - Standard API error format
// 2. { detail: "message" } - Simple detail error
// 3. { message: "message" } - Simple message error
// 4. { field_name: ["error1", "error2"] } - Field-specific errors
// 5. { non_field_errors: ["error1", "error2"] } - Non-field errors
// 6. ["error1", "error2"] - Array of errors
// All messages are displayed exactly as received from backend
export const handleApiErrors = (error) => {
  let errorMessages = [{ field: null, message: 'An unknown error occurred.' }];

  if (error.response) {
    // Error response from the server
    const { status, data } = error.response;
    
    // Handle direct response object (when data is not nested)
    const responseData = data || error.response;
    
    console.log('API Error - Status:', status, 'Data:', responseData);

    if (responseData) {
      // Handle specific error format: { detail: "message", code: "error_code" }
      if (responseData?.detail && responseData?.code) {
        errorMessages = [{ 
          field: null, 
          message: responseData.detail,
          code: responseData.code 
        }];
        
      } else if (responseData?.detail || responseData?.message) {
        // Handles { detail: "Error message" } or { message: "Error message" }
        if (Array.isArray(responseData?.message)) {
          errorMessages = responseData.message.map((message) => ({
            field: null,
            message,
          }));
        } else if (Array.isArray(responseData?.detail)) {
          errorMessages = responseData.detail.map((message) => ({
            field: null,
            message,
          }));
        } else {
          errorMessages = [{ field: null, message: responseData?.detail || responseData?.message }];
        }
      } else if (responseData?.non_field_errors && Array.isArray(responseData?.non_field_errors)) {
        errorMessages = responseData.non_field_errors.map((message) => ({
          field: null,
          message,
        }));
      } else if (Array.isArray(responseData)) {
        errorMessages = responseData.map((message) => ({ field: null, message }));
      } else if (typeof responseData === 'object' && responseData !== null) {
        errorMessages = parseErrorsWithFieldNames(responseData);
      }
    } else {
      let message;
      if (status === 403) {
        message = 'Forbidden: Access is denied.';
      } else if (status === 500) {
        message = 'Internal Server Error. Please try again later.';
      } else {
        message = `Unexpected error occurred. Status code: ${status}`;
      }
      errorMessages = [{ field: null, message }];
    }
  } else if (error?.request) {
    // Network errors or no response received
    errorMessages = [
      {
        field: null,
        message: 'No response from the server. Please check your connection.',
      },
    ];
  } else if (error?.message) {
    // JavaScript Error object
    errorMessages = [{ field: null, message: error?.message }];
  } else if (typeof error === 'string') {
    // Non-object errors
    errorMessages = [{ field: null, message: error }];
  }

  return errorMessages;
};

// Searchable Select Fetch Function Call
export const searchableSelectFetchOptions = async (url, query, additionalParams, customReturn) => {
  const apiUrl = query ? `${url}?search=${query}` : `${url}`; // No search parameter when query is empty

  const response = await adminApi.get(apiUrl, {
    params: {
      ...additionalParams,
    },
  });
  return customReturn
    ? customReturn(response)
    : response?.data?.results?.map((item) => ({
        label: item?.business_name, // Adjust based on your API response
        value: item?.id,
      }));
};
